/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import {
    bool, func, object, string,
} from 'prop-types';

import {
    Button, Grid, IconButton, makeStyles,
} from '@material-ui/core';
import { ChevronLeft } from '@material-ui/icons';

import { useLazyQuery } from '@apollo/client';
import { startOfWeek } from 'date-fns';
import EnterpriseCalendar from './EnterpriseCalendar';
import LocalFeatureLoader from '../ComponentLoader/LocalFeatureLoader';

import { calendarColorStyle } from '../Helpers/Style-Helper';
import { getOrderId, getPassportSubscriptionStatus } from '../../../../../../../state/ducks/MfeCheckout/ducks/Cart/Cart-Selectors';
import { getFeatureFlags } from '../../../../../../../state/ducks/MfeCheckout/ducks/App/App-Selectors';
import { getIsAuthenticatedStatus, getIsPassportUserLoggedIn } from '../../../../../../../state/ducks/Checkout/ducks/Global/Global-Selectors';
import {
    convertDateObjToStrDate, convertDateStringToObj, getEnterpriseCalendarData, getListDeliveryDates,
    getListHolidayDates,
} from './helpers/Calendar-Helper';
import findCalendar from '../../../../../../gql/queries/findCalendar';
import getBrandDomainById from '../../../../../../helpers/getBrandDomain';
import { GRAPHQL_ENV } from '../../../../../../gql';
import { calendarMapPresentation } from '../Helpers/Sku-helper';

const useStyles = makeStyles(
    (theme) => ({
        root: {
            margin: 0,
            color: calendarColorStyle.primary.main,
            [theme.breakpoints.down(490)]: {
                position: 'relative',
                height: '100%',
                minHeight: '602px',
            },
        },
        roorMOWO: {
            [theme.breakpoints.down(490)]: {
                minHeight: '308px',
            },
        },
        errorRoot: {
            padding: '32px',
        },
        errorMessage: {
            color: calendarColorStyle.common.error,
            fontWeight: '700',
            lineHeight: '131.25%',
            letterSpacing: '0.16px',
            textAlign: 'center',
            padding: '0 20px',
        },
        errorButton: {
            backgroundColor: calendarColorStyle.primary[800],
            color: calendarColorStyle.common.white,
            border: `1px solid ${calendarColorStyle.primary[800]}`,
            margin: '24px 0 0',
            padding: '11px 24px',
            borderRadius: '3px',
            textTransform: 'capitalize',
            fontWeight: '700',
            fontSize: '1em',
            lineHeight: '1.313em',
            width: '100%',
            boxShadow: 'none',
            [theme.breakpoints.down(408)]: {
                width: '95%',
                padding: '5px 14px',
            },
            '&:hover, &:focus, &:active': {
                backgroundColor: calendarColorStyle.common.white,
                color: calendarColorStyle.primary[800],
                boxShadow: 'none',
            },
        },
        navegationRoot: {
            margin: '0',
            justifyContent: 'flex-start',
        },
        arrowIcon: {
            margin: '0 0 24px',
            padding: '0 5px 0 0',
            fontSize: '1em',
            borderRadius: '3px',
            color: calendarColorStyle.common.arrows,
            '& svg': {
                fontSize: '24px',
            },
        },
    }),
    { name: 'EnterpriseCalendarContainer' },
);

const DEFAULT_CALENDAR_ERROR_MESSAGE = 'No delivery dates are currently available, please choose another item.';

const EnterpriseCalendarContainer = ({
    setCalendarSelectionChanged,
    handleDeliveryCalendarClose,
    isOpenCalendarConsent,
    orderItem,
    pageType,
    recipientItem,
    setErrorMessage,
    setWOMOCalendar,
}) => {
    const queryClient = useQueryClient();

    // Start month from first day.
    const currentDate = new Date();
    currentDate.setDate(1);

    const todayDateStr = convertDateObjToStrDate(currentDate);

    const classes = useStyles();

    const cartId = useSelector(getOrderId);
    const featureFlags = useSelector(getFeatureFlags);
    const isUserLoggedIn = useSelector(getIsAuthenticatedStatus);
    const isPassportUser = useSelector(getIsPassportUserLoggedIn);
    const passportSubscriptionStatus = useSelector(getPassportSubscriptionStatus);
    const userStateLocation = useSelector((state) => state.ui?.appContext?.validatedStateByZipcode || '');

    const [startDateCalendar, setStartDateCalendar] = useState(todayDateStr);
    const [listStartDatesUserNavegate, setListStartDatesUserNavegate] = useState([]);
    const [calendarListByDates, setCalendarListByDates] = useState({});

    const [forceExactDay, setForceExactDay] = useState(false);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedDateRange, setSelectedDateRange] = useState([]);
    const [selectedDeliveryDateObj, setSelectedDeliveryDateObj] = useState({});

    const [selectedHoliday, setSelectedHoliday] = useState({});
    const [holidaySelected, setHolidaySelected] = useState('Holiday Delivery');
    const [shipHolidayDetails, setShipHolidayDetails] = useState({});
    const [holidayDateCollection, setHolidayDateCollection] = useState([]);

    const isNewDeliveryCalendaV2CheckoutEnabled = featureFlags['is-new-delivery-calendar-checkout-enabled'];
    const leadTimeEnterpriseCalendarEnabled = featureFlags['which-lead-time-enterprise-calendar-enabled'] || 0; // default 5 min

    // We calculate the start date of the week from the first day of the month
    // in that way we can get the 6 weeks from the calendar response
    const startDate = convertDateStringToObj(startDateCalendar);
    const startDateStartOfWeek = startOfWeek(startDate);
    const startDateToRequest = convertDateObjToStrDate(startDateStartOfWeek);

    // Get the data from content stack is need for the calendar flowers
    let brandDomain = getBrandDomainById(orderItem.product.brandCode);
    const isInclementBannerEnabled = featureFlags['is-calendar-inclement-weather-banner-enabled'];
    const weatherBannerQuery = isInclementBannerEnabled
        ? `{\\"$or\\": [{ \\"state\\": \\"\\" }, { \\"state\\": {\\"$in\\":[\\"${recipientItem?.state || userStateLocation}\\"]} }]}`
        : null;

    // Cardisle pod doesnt exist in content stack is possible we need create this on the
    // future.
    if (brandDomain === 'cardisle') {
        brandDomain = 'celebrationsgifting';
    }

    let customerType = isUserLoggedIn ? 'R' : 'G';
    let customerTypeContentStack = 'guest';
    if (passportSubscriptionStatus || (isUserLoggedIn && isPassportUser)) {
        customerType = 'P';
        customerTypeContentStack = 'passport';
    }

    const FIND_CALENDAR_CONTENT_STACK_DATA = findCalendar(weatherBannerQuery);
    const [getCalendar, { data: cmsData }] = useLazyQuery(FIND_CALENDAR_CONTENT_STACK_DATA);

    useEffect(() => {
        const isStandaloneCard = ['GREETINGCARD-ITEM'].includes(orderItem.itemInfo.lineItemType);
        const calendarPresentationFamily = calendarMapPresentation[orderItem.product.brandCode || '1001'] || 'flower';

        if (
            (calendarPresentationFamily === 'flower' && recipientItem.zipCode)
            || (isNewDeliveryCalendaV2CheckoutEnabled && orderItem.displayCalendar)
            || isStandaloneCard
        ) {
            getCalendar({
                variables: {
                    brand: brandDomain,
                    environment: GRAPHQL_ENV,
                    productType: orderItem.product.productType,
                    userType: customerTypeContentStack,
                },
            });
        }
    }, []);

    const  { data, isLoading, error } = getEnterpriseCalendarData({
        cartId,
        customerType,
        leadTimeEnterpriseCalendarEnabled,
        isNewDeliveryCalendaV2CheckoutEnabled,
        orderItem,
        recipientItem,
        startDate: startDateToRequest,
    });

    const currentDateOnCalendar = convertDateStringToObj(startDateCalendar);
    const calendarData = data?.data?.response || null;

    const sortHolidayDetailsByDate = (holidayDetailsByCode = {}) => {
        const sortedHolidayEntries = Object.keys(holidayDetailsByCode)
            .sort((holidayCodeA, holidayCodeB) => {
                const holidayDateA = holidayDetailsByCode[holidayCodeA]?.[0]?.holidayDate?.split(' ')[0] || '';
                const holidayDateB = holidayDetailsByCode[holidayCodeB]?.[0]?.holidayDate?.split(' ')[0] || '';
                const parsedHolidayDateA = holidayDateA ? convertDateStringToObj(holidayDateA) : new Date(0);
                const parsedHolidayDateB = holidayDateB ? convertDateStringToObj(holidayDateB) : new Date(0);

                return parsedHolidayDateA - parsedHolidayDateB;
            });

        return sortedHolidayEntries.reduce((sortedHolidayDetails, holidayCode) => ({
            ...sortedHolidayDetails,
            [holidayCode]: holidayDetailsByCode[holidayCode],
        }), {});
    };

    const combineCalendarDataFromCache = () => {
        const currentListByDates = getListDeliveryDates(calendarData.calendarDates);
        const shipHolidays = calendarData.shipHolidays || [];
        const currentHolidaysByHolidayCode = getListHolidayDates(shipHolidays);
        const currentListHolidayCodes = Object.keys(currentHolidaysByHolidayCode) || [];
        const currentListHolidayDates = currentListHolidayCodes.map((code) => currentHolidaysByHolidayCode[code][0].holidayDate.split(' ')[0]);

        if (listStartDatesUserNavegate.length > 0 && leadTimeEnterpriseCalendarEnabled > 0) {
            let fullMonthsCalendarByDates = {};
            let fullListHolidayListByCode = {};
            const newlistStartDatesUserNavegate = [];

            // Look into the list of the dates save are on cache
            listStartDatesUserNavegate.forEach((date) => {
                const calendarDatesCache = queryClient.getQueryData([`foodEnterpriseCalendar-${date}`]);
                if (calendarDatesCache?.data?.response?.calendarDates) {
                    const listCacheByDates = getListDeliveryDates(calendarDatesCache.data.response.calendarDates);
                    fullMonthsCalendarByDates = {
                        ...fullMonthsCalendarByDates,
                        ...listCacheByDates,
                    };
                    newlistStartDatesUserNavegate.push(date);

                    if (calendarDatesCache?.data?.response?.shipHolidays) {
                        const listHolidayMonth = getListHolidayDates(calendarDatesCache.data.response.shipHolidays);
                        fullListHolidayListByCode = {
                            ...fullListHolidayListByCode,
                            ...listHolidayMonth,
                        };
                    }
                }
            });

            setListStartDatesUserNavegate([
                ...newlistStartDatesUserNavegate,
                startDateCalendar,
            ]);

            setCalendarListByDates({
                ...fullMonthsCalendarByDates,
                ...currentListByDates,
            });

            const shipHolidayByCode = sortHolidayDetailsByDate({
                ...fullListHolidayListByCode,
                ...currentHolidaysByHolidayCode,
            });
            const listHolidayCodes = Object.keys(shipHolidayByCode);
            const listHolidayDates = listHolidayCodes.map((code) => shipHolidayByCode[code][0].holidayDate.split(' ')[0]);
            setShipHolidayDetails(shipHolidayByCode);
            setHolidayDateCollection(listHolidayDates);
        } else {
            if (!listStartDatesUserNavegate.includes(startDateCalendar)) {
                setListStartDatesUserNavegate([startDateCalendar]);
            }
            // setup data when open calendar
            setCalendarListByDates(currentListByDates);
            // set list of holidays
            setShipHolidayDetails(sortHolidayDetailsByDate(currentHolidaysByHolidayCode));
            setHolidayDateCollection(currentListHolidayDates);
        }
    };

    useEffect(() => {
        if (error?.response?.data?.errorMessage) {
            setErrorMessage(error.response.data.errorMessage);
        }
    }, [error]);

    useEffect(() => {
        if (data?.data?.response?.calendarDates) {
            if (['WO', 'MO'].includes(data.data.response.deliveryIndicator)) {
                setWOMOCalendar(true);
            }

            combineCalendarDataFromCache();
        }
    }, [data]);

    // console.log('=====EnterpriseCalendar=======');
    // console.log('isLoading', isLoading);
    // console.log('data', data);
    // console.log('error', error?.message);
    // console.log('calendarData', calendarData);
    // console.log('listStartDatesUserNavegate', listStartDatesUserNavegate);
    // console.log('calendarListByDates', calendarListByDates);
    // console.log('startDateCalendar', startDateCalendar);
    // console.log('forceExactDay', forceExactDay);
    // console.log('selectedDate', selectedDate);
    // console.log('selectedDateRange', selectedDateRange);
    // console.log('selectedDeliveryDateObj', selectedDeliveryDateObj);
    // console.log('selectedHoliday', selectedHoliday);
    // console.log('shipHolidayDetails', shipHolidayDetails);
    // console.log('holidaySelected', holidaySelected);
    // console.log('holidayDateCollection', holidayDateCollection);
    // console.log('pageType', pageType);
    // console.log('cmsData', cmsData?.findCalendar);
    // console.log('=====End - EnterpriseCalendar=======');

    const handleGoBackClick = () => {
        console.log('holidaySelected', holidaySelected);
        if (holidaySelected !== 'Holiday Delivery') {
            setHolidaySelected('Holiday Delivery');
            setSelectedHoliday({});
            setHolidayDateCollection([]);
        }
        setStartDateCalendar(todayDateStr);
    };

    return (
        <>
            {isLoading &&  <LocalFeatureLoader />}
            <Grid className={`${classes.root} ${['WO', 'MO'].includes(data?.data?.response?.deliveryIndicator) ? classes.roorMOWO : ''}`}>
                {
                    !!error
                    && (
                        <Grid container className={classes.errorRoot}>
                            {
                                currentDate !== startDateCalendar
                                && (
                                    currentDateOnCalendar.getFullYear() > currentDate.getFullYear()
                                || (
                                    currentDateOnCalendar.getFullYear() === currentDate.getFullYear()
                                    && currentDateOnCalendar.getMonth() > currentDate.getMonth()
                                )
                                )
                                && (
                                    <Grid container className={classes.navegationRoot}>
                                        <IconButton
                                            id="enterprise-calendar-previous-button"
                                            aria-label="Previous Month"
                                            onClick={handleGoBackClick}
                                            className={classes.arrowIcon}
                                            data-test="enterprise-calendar-previous-button"
                                        >
                                            <ChevronLeft /> Go Back
                                        </IconButton>
                                    </Grid>
                                )
                            }
                            <Grid item xs={12} className={classes.errorMessage}>
                                {
                                    (error?.response?.data?.errorMessage)
                                        ? (
                                            <>
                                                {
                                                    ['No delivery dates are available'].includes(error.response.data.errorMessage)
                                                        ? DEFAULT_CALENDAR_ERROR_MESSAGE
                                                        : error.response.data.errorMessage || DEFAULT_CALENDAR_ERROR_MESSAGE
                                                }
                                            </>
                                        ) : (
                                            <>{
                                            error?.message
                                            && !isLoading
                                            && !data
                                            && (
                                                `${DEFAULT_CALENDAR_ERROR_MESSAGE} - (${error?.message})`
                                            )
                                            }
                                            </>
                                        )
                                }
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    className={classes.errorButton}
                                    color="primary"
                                    data-test="checkout-close-button"
                                    id="checkout-close-button"
                                    fullWidth
                                    onClick={handleDeliveryCalendarClose}
                                    variant="contained"
                                >
                                    Close
                                </Button>
                            </Grid>
                        </Grid>
                    )
                }
                {
                    !isLoading
                    && calendarData?.calendarDates?.length > 0
                    && Object.keys(calendarListByDates)?.length > 0
                    && (
                        <EnterpriseCalendar
                            calendarData={calendarData || {}}
                            calendarListByDates={calendarListByDates || {}}
                            cmsData={cmsData?.findCalendar?.content || {}}
                            handleDeliveryCalendarClose={handleDeliveryCalendarClose}
                            forceExactDay={forceExactDay}
                            holidayDateCollection={holidayDateCollection}
                            holidaySelected={holidaySelected}
                            isOpenCalendarConsent={isOpenCalendarConsent}
                            orderItem={orderItem}
                            setCalendarSelectionChanged={setCalendarSelectionChanged}
                            setStartDateCalendar={setStartDateCalendar}
                            startDateCalendar={startDateCalendar}
                            pageType={pageType}
                            recipientItem={recipientItem}
                            selectedDate={selectedDate}
                            selectedDateRange={selectedDateRange}
                            selectedDeliveryDateObj={selectedDeliveryDateObj}
                            selectedHoliday={selectedHoliday}
                            setForceExactDay={setForceExactDay}
                            setHolidaySelected={setHolidaySelected}
                            setSelectedDate={setSelectedDate}
                            setSelectedDateRange={setSelectedDateRange}
                            setSelectedDeliveryDateObj={setSelectedDeliveryDateObj}
                            setSelectedHoliday={setSelectedHoliday}
                            setShipHolidayDetails={setShipHolidayDetails}
                            shipHolidayDetails={shipHolidayDetails}
                        />
                    )
                }
            </Grid>
        </>
    );
};

EnterpriseCalendarContainer.propTypes = {
    handleDeliveryCalendarClose: func.isRequired,
    isOpenCalendarConsent: bool.isRequired,
    orderItem: object.isRequired,
    pageType: string.isRequired,
    recipientItem: object.isRequired,
    setCalendarSelectionChanged: func.isRequired,
    setErrorMessage: func.isRequired,
    setWOMOCalendar: func.isRequired,
};

EnterpriseCalendarContainer.defaultProps = {

};

export default EnterpriseCalendarContainer;
